import { useState } from 'react';

type HelpTab = 'getting-started' | 'how-to' | 'data-model' | 'troubleshooting' | 'checklists';

const tabs: Array<{ id: HelpTab; label: string; icon: string }> = [
  { id: 'getting-started', label: 'Getting Started', icon: '🚀' },
  { id: 'how-to', label: 'How-To Guides', icon: '📋' },
  { id: 'data-model', label: 'Data Model', icon: '🔗' },
  { id: 'troubleshooting', label: 'Troubleshooting', icon: '🔧' },
  { id: 'checklists', label: 'Checklists', icon: '✅' },
];

export function AdminHelpPage() {
  const [activeTab, setActiveTab] = useState<HelpTab>('getting-started');

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <span className="text-2xl">❓</span>
          Help & Training
        </h1>
        <p className="mt-2 text-gray-600">
          Guides, references, and troubleshooting for managing the EDI Label Service.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-4 overflow-x-auto" aria-label="Help tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`whitespace-nowrap pb-3 px-3 border-b-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-1">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="prose prose-sm max-w-none">
        {activeTab === 'getting-started' && <GettingStartedTab />}
        {activeTab === 'how-to' && <HowToTab />}
        {activeTab === 'data-model' && <DataModelTab />}
        {activeTab === 'troubleshooting' && <TroubleshootingTab />}
        {activeTab === 'checklists' && <ChecklistsTab />}
      </div>
    </div>
  );
}

// ─── Tab Components ─────────────────────────────────────────

function GettingStartedTab() {
  return (
    <div className="space-y-6">
      <Section title="Core Concepts">
        <p className="text-gray-600">
          The EDI Label Service is a multi-tenant platform for generating ZPL shipping labels. Understanding three key entities is essential:
        </p>
        <div className="grid md:grid-cols-3 gap-4 mt-4">
          <ConceptCard
            title="Retailers"
            icon="🏪"
            description="Companies whose labels you generate (Walmart, Buc-ee's, etc.). Each retailer has an industry, templates, and label specifications."
          />
          <ConceptCard
            title="Templates"
            icon="📄"
            description="ZPL label designs using Handlebars syntax. Templates belong to a retailer and go through a lifecycle: draft → approved → active."
          />
          <ConceptCard
            title="Tenants"
            icon="👥"
            description="Your API consumers (clients). Each tenant gets API keys and is assigned specific templates they're allowed to use."
          />
        </div>
      </Section>

      <Section title="The Onboarding Flow">
        <div className="bg-gray-50 rounded-lg p-4">
          <ol className="space-y-3 text-sm text-gray-700">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-bold">1</span>
              <span><strong>Create a Retailer</strong> — Add the retailer entity with their industry and contact info.</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-bold">2</span>
              <span><strong>Create Templates</strong> — Either manually write ZPL templates or use the AI onboarding pipeline to auto-generate them.</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-bold">3</span>
              <span><strong>Approve & Activate</strong> — Review templates, then approve and activate them to make them usable.</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-bold">4</span>
              <span><strong>Create a Tenant</strong> — Set up the client organization with a pricing plan.</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-bold">5</span>
              <span><strong>Generate API Key</strong> — Create an API key for the tenant (shown only once — copy immediately!).</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-bold">6</span>
              <span><strong>Assign Templates</strong> — Link active templates to the tenant so they can generate labels.</span>
            </li>
          </ol>
        </div>
      </Section>

      <Section title="Navigation Quick Reference">
        <div className="grid md:grid-cols-2 gap-3">
          <NavCard to="/admin/retailers" icon="🏪" title="Retailers" description="Create, edit, and manage retailer entities" />
          <NavCard to="/admin/templates" icon="📄" title="Templates" description="Create, approve, activate, and assign ZPL templates" />
          <NavCard to="/admin/tenants" icon="👥" title="Tenants" description="Manage client organizations and API access" />
          <NavCard to="/admin/onboarding" icon="🚀" title="Onboarding" description="AI-powered template generation pipeline" />
          <NavCard to="/admin/billing" icon="💰" title="Billing" description="View usage costs and pricing plans" />
          <NavCard to="/admin/monitoring" icon="🔍" title="Monitoring" description="API health, latency, and error tracking" />
        </div>
      </Section>
    </div>
  );
}

function HowToTab() {
  return (
    <div className="space-y-6">
      <HowToSection
        title="Create a Retailer"
        steps={[
          'Go to Retailers page and click "Add Retailer".',
          'Enter a Retailer ID (lowercase, kebab-case — e.g., walmart, buc-ees). This cannot be changed later.',
          'Select the industry and fill in display name, website, and contact email.',
          'Click "Create Retailer". The retailer is immediately active.',
        ]}
      />

      <HowToSection
        title="Create a Template (Manual)"
        steps={[
          'Go to Templates page and click "Create Template".',
          'Select the tenant scope (Global or specific tenant).',
          'Fill in industry, company (must match a Retailer ID), template name, and label type.',
          'Paste your ZPL content using Handlebars syntax: {{variableName}} for fields.',
          'Set label dimensions (width, height in inches) and DPI.',
          'Click "Create". The template starts in "draft" status.',
        ]}
      />

      <HowToSection
        title="Create Templates (AI Pipeline)"
        steps={[
          'Go to Onboarding page and click "+ New Partner".',
          'Enter the partner name and optionally select an industry and provide spec URLs.',
          'Click "Create Job". The job enters the pipeline queue.',
          'Click "Process Queue" to start processing.',
          'Monitor the job through stages: sourcing → extraction → review → composition → review → provisioning.',
          'Review and approve generated templates at each review stage.',
        ]}
      />

      <HowToSection
        title="Approve & Activate a Template"
        steps={[
          'Go to Templates page and select a template in "draft" status.',
          'Review the ZPL content in the detail panel.',
          'Click "Approve Version X.X.X" to move it to "approved" status.',
          'Click "Activate Version X.X.X" to make it live and usable for label generation.',
          'Only "active" templates can generate labels.',
        ]}
      />

      <HowToSection
        title="Create a Tenant & Grant API Access"
        steps={[
          'Go to Tenants page and click "Create".',
          'Fill in the tenant name, email, and pricing plan.',
          'Click into the new tenant\'s detail page.',
          'Go to the "API Keys" tab and create a new key. Copy it immediately — it\'s shown only once!',
          'Go to the "Templates" tab and click "Assign Template" to give the tenant access to specific templates.',
          'Send the client their API key and the Getting Started documentation.',
        ]}
      />

      <HowToSection
        title="Assign a Template to a Tenant"
        steps={[
          'Option A: From Templates detail panel, click "Assign to Tenant" and select the tenant.',
          'Option B: From Tenant detail → Templates tab, click "+ Assign Template" and select from available templates.',
          'Bulk assignment: Use the bulk assign feature to assign multiple templates at once.',
          'A template must be "active" for the tenant to generate labels with it.',
        ]}
      />
    </div>
  );
}

function DataModelTab() {
  return (
    <div className="space-y-6">
      <Section title="Entity Relationships">
        <div className="bg-gray-50 rounded-lg p-6 text-center font-mono text-sm">
          <div className="inline-block text-left">
            <pre className="text-gray-700">{`
┌─────────────┐       ┌──────────────┐       ┌────────────┐
│  Retailer   │ 1───* │   Template   │ *───* │   Tenant   │
│             │       │              │       │            │
│ retailerId  │       │ templateKey  │       │ tenantId   │
│ name        │       │ company ─────┘       │ name       │
│ industry    │       │ labelType    │       │ apiKeys[]  │
│ status      │       │ status       │       │ status     │
│ templateCnt │       │ zplContent   │       │            │
└─────────────┘       │ version      │       └────────────┘
                      └──────────────┘
                             │
                    ┌────────┴────────┐
                    │  Assignment     │
                    │ templateKey +   │
                    │ tenantId        │
                    └─────────────────┘`}
            </pre>
          </div>
        </div>
      </Section>

      <Section title="Key Relationships">
        <dl className="space-y-4">
          <div className="bg-white border rounded-lg p-4">
            <dt className="font-medium text-gray-900">Retailer → Templates</dt>
            <dd className="mt-1 text-sm text-gray-600">
              One-to-many. A template's <code className="bg-gray-100 px-1 rounded">company</code> field must exactly match a retailer's <code className="bg-gray-100 px-1 rounded">retailerId</code>. This is the <em>only</em> link between them.
            </dd>
          </div>
          <div className="bg-white border rounded-lg p-4">
            <dt className="font-medium text-gray-900">Templates ↔ Tenants</dt>
            <dd className="mt-1 text-sm text-gray-600">
              Many-to-many via the <strong>Assignments</strong> table. A template can be assigned to multiple tenants, and a tenant can have multiple templates. Tenants only see their assigned templates in the API.
            </dd>
          </div>
          <div className="bg-white border rounded-lg p-4">
            <dt className="font-medium text-gray-900">Template Key Format</dt>
            <dd className="mt-1 text-sm text-gray-600">
              <code className="bg-gray-100 px-2 py-1 rounded text-xs">{'industry--company--templateName'}</code><br />
              Example: <code className="bg-gray-100 px-2 py-1 rounded text-xs">retail--walmart--shipping-4x6</code>
            </dd>
          </div>
          <div className="bg-white border rounded-lg p-4">
            <dt className="font-medium text-gray-900">Template Lifecycle</dt>
            <dd className="mt-1 text-sm text-gray-600">
              <span className="inline-flex items-center gap-1">
                <StatusPill color="gray">draft</StatusPill>
                →
                <StatusPill color="blue">approved</StatusPill>
                →
                <StatusPill color="green">active</StatusPill>
              </span>
              <br className="mt-1" />
              Only <strong>active</strong> templates can generate labels. Templates can be assigned to tenants at any status, but label generation requires active.
            </dd>
          </div>
        </dl>
      </Section>
    </div>
  );
}

function TroubleshootingTab() {
  return (
    <div className="space-y-4">
      <TroubleshootItem
        issue="Template returns 404 when generating labels"
        causes={['Template is not in "active" status', 'Template key is misspelled', 'Template is not assigned to the requesting tenant']}
        fixes={['Check template status on Templates page — approve and activate it', 'Verify the template key matches exactly (case-sensitive)', 'Go to Tenant detail → Templates tab and verify the assignment']}
      />
      <TroubleshootItem
        issue="Tenant gets 401 Unauthorized"
        causes={['API key is missing or incorrect', 'API key has been revoked', 'Tenant status is not "active"']}
        fixes={['Verify the x-api-key header value', 'Check the API Keys tab — create a new key if needed', 'Change tenant status to "active" in the Overview tab']}
      />
      <TroubleshootItem
        issue="Template count shows 0 for a retailer"
        causes={['Templates exist but their "company" field doesn\'t match the retailer ID', 'Templates haven\'t been uploaded yet']}
        fixes={['Click "View Templates →" in the retailer detail to see what\'s linked', 'Verify the template\'s company field matches the retailer ID exactly']}
      />
      <TroubleshootItem
        issue="AI pipeline job stuck in one stage"
        causes={['External API rate limiting', 'Network/service errors', 'Invalid spec URLs']}
        fixes={['Check the job detail page for error messages', 'Re-queue the job from the Onboarding page', 'If spec URLs are bad, edit the job and provide valid URLs or remove them']}
      />
      <TroubleshootItem
        issue="Preview shows blank or CSP error"
        causes={['Preview endpoint not enabled in Azure', 'CSP doesn\'t include blob: in img-src', 'Labelary rendering service is down']}
        fixes={['Set ENABLE_PREVIEW_ENDPOINT=true in Azure Function App settings', 'Ensure CSP includes "blob:" in img-src directive', 'Try again later or use raw ZPL generation instead']}
      />
      <TroubleshootItem
        issue="Label has {{undefined}} or missing data"
        causes={['Field name doesn\'t match what the template expects', 'Nested path is wrong (e.g., shipTo.name vs shipFrom.name)', 'Required field not provided in the request']}
        fixes={['Check the template\'s field definitions on the Templates page', 'Verify exact field names (case-sensitive, dot-notation for nested objects)', 'Include all required fields in the data payload']}
      />
      <TroubleshootItem
        issue="Barcode appears malformed on label"
        causes={['SSCC is not exactly 18 digits', 'Invalid check digit', 'UPC is not exactly 12 digits']}
        fixes={['SSCC format: 0 + 7-digit company prefix + 9-digit serial + 1 check digit = 18 digits', 'Use an SSCC check digit calculator', 'UPC-A must be exactly 12 digits including check digit']}
      />
      <TroubleshootItem
        issue="Batch request partially fails"
        causes={['Some labels in the batch have validation errors', 'One template is inactive while others are active']}
        fixes={['Check each results[].success flag in the response', 'Fix individual label data issues per the error details', 'Batch allows partial success — successful labels are still returned']}
      />
    </div>
  );
}

function ChecklistsTab() {
  return (
    <div className="space-y-8">
      <Section title="New Retailer Checklist">
        <ChecklistGroup items={[
          { label: 'Create retailer entity (Retailers → Add Retailer)', detail: 'Set retailer ID, name, industry, contact info' },
          { label: 'Create or generate templates', detail: 'Manual: Templates → Create Template | AI: Onboarding → New Partner' },
          { label: 'Review template ZPL content', detail: 'Verify variables, barcodes, formatting' },
          { label: 'Approve templates', detail: 'Templates detail → Approve Version' },
          { label: 'Activate templates', detail: 'Templates detail → Activate Version' },
          { label: 'Test labels with sample data', detail: 'Client View → Test Label page' },
          { label: 'Verify template count on retailer', detail: 'Retailers → select retailer → check template count' },
        ]} />
      </Section>

      <Section title="New Tenant Checklist">
        <ChecklistGroup items={[
          { label: 'Create tenant', detail: 'Tenants → Create → name, email, pricing plan' },
          { label: 'Activate tenant', detail: 'Tenant detail → Overview → set status to Active' },
          { label: 'Create API key', detail: 'Tenant detail → API Keys → Create. Copy immediately!' },
          { label: 'Assign templates', detail: 'Tenant detail → Templates → Assign. Select which templates this tenant can use' },
          { label: 'Test in client view', detail: 'Switch to Client View and verify templates appear' },
          { label: 'Send client documentation', detail: 'Share Getting Started guide, API Integration guide, and Label Testing guide' },
          { label: 'Monitor first usage', detail: 'Billing / Usage pages — verify client is generating labels successfully' },
        ]} />
      </Section>
    </div>
  );
}

// ─── Shared Sub-components ────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-4">{title}</h2>
      {children}
    </div>
  );
}

function ConceptCard({ title, icon, description }: { title: string; icon: string; description: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl">{icon}</span>
        <h3 className="font-semibold text-gray-900">{title}</h3>
      </div>
      <p className="text-sm text-gray-600">{description}</p>
    </div>
  );
}

function NavCard({ to, icon, title, description }: { to: string; icon: string; title: string; description: string }) {
  return (
    <a
      href={to}
      className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
    >
      <span className="text-xl">{icon}</span>
      <div>
        <h3 className="font-medium text-gray-900">{title}</h3>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
    </a>
  );
}

function HowToSection({ title, steps }: { title: string; steps: string[] }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5">
      <h3 className="font-semibold text-gray-900 mb-3">{title}</h3>
      <ol className="space-y-2 text-sm text-gray-700">
        {steps.map((step, i) => (
          <li key={i} className="flex gap-2">
            <span className="flex-shrink-0 w-5 h-5 bg-gray-100 text-gray-600 rounded-full flex items-center justify-center text-xs font-medium">{i + 1}</span>
            <span>{step}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function StatusPill({ color, children }: { color: 'gray' | 'blue' | 'green'; children: React.ReactNode }) {
  const colors = {
    gray: 'bg-gray-100 text-gray-700',
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-green-100 text-green-700',
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded ${colors[color]}`}>
      {children}
    </span>
  );
}

function TroubleshootItem({ issue, causes, fixes }: { issue: string; causes: string[]; fixes: string[] }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-gray-200 rounded-lg">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 rounded-lg"
      >
        <span className="font-medium text-gray-900">{issue}</span>
        <span className="text-gray-400">{isOpen ? '▼' : '▶'}</span>
      </button>
      {isOpen && (
        <div className="px-4 pb-4 grid md:grid-cols-2 gap-4">
          <div>
            <h4 className="text-xs font-medium text-red-600 uppercase mb-2">Possible Causes</h4>
            <ul className="space-y-1 text-sm text-gray-600">
              {causes.map((c, i) => <li key={i} className="flex gap-1"><span className="text-red-400">•</span> {c}</li>)}
            </ul>
          </div>
          <div>
            <h4 className="text-xs font-medium text-green-600 uppercase mb-2">Fixes</h4>
            <ul className="space-y-1 text-sm text-gray-600">
              {fixes.map((f, i) => <li key={i} className="flex gap-1"><span className="text-green-400">•</span> {f}</li>)}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function ChecklistGroup({ items }: { items: Array<{ label: string; detail: string }> }) {
  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-start gap-3 p-3 bg-white border border-gray-200 rounded-lg">
          <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-bold mt-0.5">{i + 1}</span>
          <div>
            <p className="font-medium text-gray-900 text-sm">{item.label}</p>
            <p className="text-xs text-gray-500">{item.detail}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
